"use client";

import { showAutoOrderToast } from "@/store/autoOrderToastSlice";
import { clearSessionExpired } from "@/store/sessionExpirySlice";
import type { AppState } from "@/store/store";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

/**
 * Global handler for the "session expired" flow. PI-1372.
 *
 * Mounted once at the layout level (`standardPage.tsx`). Watches the
 * `sessionExpirySlice` for the flag the `baseApi` 401 interceptor sets,
 * then:
 *
 * 1. Dispatches a user-visible toast ("Please sign in to continue").
 * 2. Calls `useRouter().push('/login?origin=...')` with the originally
 *    attempted path.
 * 3. Clears the slice flag so a subsequent re-login + re-expiry can fire
 *    again cleanly.
 *
 * This is the single owner of session-expired UX for Auto Order pages
 * — individual pages must NOT add their own 401 redirect effects.
 */
const SessionExpiryHandler = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { expired, originPath } = useSelector((state: AppState) => state.sessionExpirySlice);

  useEffect(() => {
    if (!expired) return;
    const target = originPath ?? "/";
    dispatch(
      showAutoOrderToast({
        toastMessage: "Your session has ended",
        toastSubMessage: "Sign in again to continue.",
        toastType: "info",
        // This toast exists to explain the redirect below, so it has to outlive it.
        persistAcrossNavigation: true,
      }),
    );
    router.push(`/login?origin=${encodeURIComponent(target)}`);
    dispatch(clearSessionExpired());
  }, [expired, originPath, dispatch, router]);

  return null;
};

export default SessionExpiryHandler;
